import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const DICTIONARIES = [
    { type: 'awareness_sources', title: 'Откуда узнали', hint: 'Ставится вручную при опросе. Заявки из Яндекс.Билетов сами по себе не «Яндекс Афиша».' },
    { type: 'payment_methods', title: 'Способы оплаты', hint: 'Справочник для кассы: наличные, карта, СБП, онлайн, Яндекс, по счету.' },
    { type: 'menu_categories', title: 'Категории меню', hint: 'Категории для блюд и кафе.' },
    { type: 'souvenir_categories', title: 'Категории сувениров', hint: 'Категории для справочника сувенирной продукции.' }
];

const SLOT_TYPES = ['Свидание', 'Индивидуальная экскурсия', 'Школьная группа', 'Тренинг'];

const emptyDraft = {
    name: '',
    price: '',
    kind: 'ticket',
    day_kind: 'weekday',
    product_type: '',
    time_until: ''
};

function SettingsPanel({ token }) {
    const [optionsByType, setOptionsByType] = useState({});
    const [drafts, setDrafts] = useState({});
    const [prices, setPrices] = useState([]);
    const [savingId, setSavingId] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newRow, setNewRow] = useState(emptyDraft);
    const [editOption, setEditOption] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    const loadDictionary = async (type) => {
        const response = await apiFetch(`${API_URL}/api/settings/options/${type}`);
        const data = await response.json();
        if (data.success) {
            setOptionsByType((prev) => ({ ...prev, [type]: data.options || [] }));
        }
    };

    const loadPrices = async () => {
        const response = await apiFetch(`${API_URL}/api/settings/price-list`);
        const data = await response.json();
        if (data.success) {
            setPrices(data.prices || []);
        }
    };

    const loadAll = async () => {
        await Promise.all(DICTIONARIES.map((dictionary) => loadDictionary(dictionary.type)));
        await loadPrices();
    };

    useEffect(() => {
        loadAll();
    }, []);

    const updateRow = (id, patch) => {
        setPrices((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    };

    const handleSaveRow = async (row) => {
        setSavingId(row.id);
        try {
            const response = await apiFetch(`${API_URL}/api/settings/price-list/${row.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(row)
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось сохранить');
            } else {
                await loadPrices();
            }
        } catch (error) {
            console.error('Error saving price:', error);
            alert('Не удалось сохранить цену');
        } finally {
            setSavingId(null);
        }
    };

    const handleAddRow = async () => {
        const name = (newRow.name || '').trim();
        if (!name) {
            alert('Введите название');
            return;
        }
        setAdding(true);
        try {
            const response = await apiFetch(`${API_URL}/api/settings/price-list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...newRow,
                    product_type: newRow.kind === 'slot' ? (newRow.product_type || name) : null,
                    time_until: newRow.time_until || null
                })
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось добавить');
            } else {
                setNewRow(emptyDraft);
                await loadPrices();
            }
        } catch (error) {
            console.error('Error adding price:', error);
            alert('Не удалось добавить цену');
        } finally {
            setAdding(false);
        }
    };

    const handleDeleteRow = async (id) => {
        if (!window.confirm('Убрать эту позицию из прайса?')) {
            return;
        }
        const response = await apiFetch(`${API_URL}/api/settings/price-list/${id}`, { method: 'DELETE' });
        const data = await response.json();
        if (data.success) {
            await loadPrices();
        }
    };

    const handleAdd = async (type) => {
        const value = (drafts[type] || '').trim();
        if (!value) return;

        const response = await apiFetch(`${API_URL}/api/settings/options/${type}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value, label: value })
        });
        const data = await response.json();
        if (data.success) {
            setDrafts((prev) => ({ ...prev, [type]: '' }));
            await loadDictionary(type);
        }
    };

    const handleDelete = async (type, value) => {
        const response = await apiFetch(`${API_URL}/api/settings/options/${type}/${encodeURIComponent(value)}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (data.success) {
            await loadDictionary(type);
        }
    };

    const openEditOption = (type, option) => {
        setEditOption({ type, option });
        setEditValue(option.label || option.value);
    };

    const handleSaveOption = async () => {
        if (!editOption) return;
        const { type, option } = editOption;
        const nextValue = (editValue || '').trim();
        if (!nextValue || nextValue === (option.label || option.value)) {
            setEditOption(null);
            return;
        }

        const message = type === 'awareness_sources'
            ? `Переименовать «${option.label || option.value}» в «${nextValue}»?\n\nСтарые записи гостей тоже будут переписаны, иначе в статистике появятся две отдельные строки.`
            : `Переименовать «${option.label || option.value}» в «${nextValue}»?`;
        if (!window.confirm(message)) {
            return;
        }

        setEditSaving(true);
        try {
            const response = await apiFetch(
                `${API_URL}/api/settings/options/${type}/${encodeURIComponent(option.value)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ value: nextValue, label: nextValue })
                }
            );
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось переименовать');
                return;
            }
            setEditOption(null);
            await loadDictionary(type);
        } catch (error) {
            console.error('Error renaming option:', error);
            alert('Не удалось переименовать');
        } finally {
            setEditSaving(false);
        }
    };

    const renderPriceTable = (kind, title, hint) => {
        const rows = prices.filter((row) => row.kind === kind);
        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 820 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 280 }}>Наименование</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Стоимость</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>День</TableCell>
                                    {kind === 'slot' && <TableCell sx={{ minWidth: 220 }}>Тип слота</TableCell>}
                                    {kind === 'ticket' && <TableCell sx={{ minWidth: 140 }}>До часа</TableCell>}
                                    <TableCell sx={{ minWidth: 110 }} align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={row.name}
                                                onChange={(e) => updateRow(row.id, { name: e.target.value })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={row.price}
                                                onChange={(e) => updateRow(row.id, { price: e.target.value })}
                                                inputProps={{ min: 0, step: 50 }}
                                                InputProps={{ endAdornment: <Typography sx={{ ml: 0.5 }}>₽</Typography> }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                size="small"
                                                fullWidth
                                                value={row.day_kind || 'any'}
                                                onChange={(e) => updateRow(row.id, { day_kind: e.target.value })}
                                            >
                                                <MenuItem value="weekday">Будни</MenuItem>
                                                <MenuItem value="weekend">Выходные</MenuItem>
                                                <MenuItem value="any">Любой день</MenuItem>
                                            </Select>
                                        </TableCell>
                                        {kind === 'slot' && (
                                            <TableCell>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    value={row.product_type || ''}
                                                    onChange={(e) => updateRow(row.id, { product_type: e.target.value })}
                                                >
                                                    {SLOT_TYPES.map((type) => (
                                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                                    ))}
                                                </Select>
                                            </TableCell>
                                        )}
                                        {kind === 'ticket' && (
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="time"
                                                    value={row.time_until ? String(row.time_until).slice(0, 5) : ''}
                                                    onChange={(e) => updateRow(row.id, { time_until: e.target.value || null })}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                disabled={savingId === row.id}
                                                onClick={() => handleSaveRow(row)}
                                                title="Сохранить"
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteRow(row.id)}
                                                title="Удалить"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {rows.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Пока пусто — добавьте строку ниже.
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
                Настройки CRM
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Прайс как в таблице экскурсий: билеты — за человека, индивидуальная / свидание / школьная — за весь слот.
                Чтобы изменить цену, поправьте число и нажмите дискету. Чтобы добавить — форма внизу блока.
            </Typography>

            {renderPriceTable(
                'ticket',
                'Билеты экскурсии',
                'Цена за одного человека. Будни / выходные подставляются по дате слота. «До часа» — например студенческий только до 18:00.'
            )}
            {renderPriceTable(
                'slot',
                'Тарифы слота',
                'Одна сумма за всю запись. Число гостей слот не умножает. Школьная — тоже фиксированная, сумму можно проставить здесь.'
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Добавить позицию</Typography>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Наименование"
                                value={newRow.name}
                                onChange={(e) => setNewRow((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Стоимость"
                                value={newRow.price}
                                onChange={(e) => setNewRow((prev) => ({ ...prev, price: e.target.value }))}
                                InputProps={{ endAdornment: <Typography sx={{ ml: 0.5 }}>₽</Typography> }}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Тип</InputLabel>
                                <Select
                                    label="Тип"
                                    value={newRow.kind}
                                    onChange={(e) => setNewRow((prev) => ({ ...prev, kind: e.target.value }))}
                                >
                                    <MenuItem value="ticket">Билет (за человека)</MenuItem>
                                    <MenuItem value="slot">Слот (за запись)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>День</InputLabel>
                                <Select
                                    label="День"
                                    value={newRow.day_kind}
                                    onChange={(e) => setNewRow((prev) => ({ ...prev, day_kind: e.target.value }))}
                                >
                                    <MenuItem value="weekday">Будни</MenuItem>
                                    <MenuItem value="weekend">Выходные</MenuItem>
                                    <MenuItem value="any">Любой день</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {newRow.kind === 'slot' && (
                            <Grid item xs={6} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Тип слота</InputLabel>
                                    <Select
                                        label="Тип слота"
                                        value={newRow.product_type}
                                        onChange={(e) => setNewRow((prev) => ({ ...prev, product_type: e.target.value }))}
                                    >
                                        {SLOT_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {newRow.kind === 'ticket' && (
                            <Grid item xs={6} md={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="time"
                                    label="До часа"
                                    value={newRow.time_until}
                                    onChange={(e) => setNewRow((prev) => ({ ...prev, time_until: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddRow}
                                disabled={adding}
                            >
                                Добавить
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={2}>
                {DICTIONARIES.map((dictionary) => (
                    <Grid item xs={12} md={6} key={dictionary.type}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                    {dictionary.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {dictionary.hint}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Новое значение"
                                        value={drafts[dictionary.type] || ''}
                                        onChange={(e) => setDrafts((prev) => ({ ...prev, [dictionary.type]: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAdd(dictionary.type);
                                            }
                                        }}
                                    />
                                    <Button variant="contained" onClick={() => handleAdd(dictionary.type)}>
                                        Добавить
                                    </Button>
                                </Box>

                                <Paper variant="outlined" sx={{ p: 1.5, minHeight: 96 }}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {(optionsByType[dictionary.type] || []).map((option) => (
                                            <Chip
                                                key={`${dictionary.type}-${option.value}`}
                                                label={option.label || option.value}
                                                variant={option.is_default ? 'outlined' : 'filled'}
                                                onClick={() => openEditOption(dictionary.type, option)}
                                                icon={<EditIcon sx={{ fontSize: 16 }} />}
                                                onDelete={option.is_default ? undefined : () => handleDelete(dictionary.type, option.value)}
                                                deleteIcon={option.is_default ? undefined : <DeleteIcon />}
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={Boolean(editOption)} onClose={() => setEditOption(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Редактировать значение</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Название"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveOption();
                            }
                        }}
                        helperText={
                            editOption?.type === 'awareness_sources'
                                ? 'Старые записи гостей будут переписаны на новое название'
                                : ' '
                        }
                        inputProps={{ maxLength: editOption?.type === 'awareness_sources' ? 50 : 250 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOption(null)} disabled={editSaving}>
                        Отмена
                    </Button>
                    <Button variant="contained" onClick={handleSaveOption} disabled={editSaving}>
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SettingsPanel;
